import { Field, Formik, Form } from 'formik';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import { Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import useFTPSettings from './hooks/useFTPSettings';
import type { FTPGallerySettings } from './hooks/useFTPSettings';

interface FTPSettingsProps {
    galleryId: number;
}

export default function FTPSettings({ galleryId }: FTPSettingsProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [settings, setSettings] = useState<FTPGallerySettings | null>(null);
    const { getFTPSettings, saveFTPSettingsWithPassword, listFTPDirectories } =
        useFTPSettings();

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                const settings: FTPGallerySettings = await getFTPSettings(
                    galleryId,
                );
                if (isMounted) {
                    setSettings(settings);
                }
            } catch (error) {
                console.error(error);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        load();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!settings) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-lg text-red-600">
                    Failed to load FTP settings
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-base font-semibold text-foreground">
                    FTP Sync Settings
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                    Configure FTP server settings for automatic photo
                    synchronization.
                </p>
            </div>

            <Formik
                initialValues={settings}
                onSubmit={async (values, actions) => {
                    const wasSuccessful = await saveFTPSettingsWithPassword(
                        galleryId,
                        values,
                    );
                    actions.setSubmitting(false);

                    if (wasSuccessful) {
                        toast.success('FTP settings saved successfully');
                    } else {
                        toast.error('Failed to save FTP settings');
                    }
                }}
            >
                {(props) => (
                    <Form
                        className="space-y-6"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        {/* Connection Settings */}
                        <div className="space-y-4">
                            <div>
                                <h4 className="text-md font-medium text-foreground">
                                    Connection Settings
                                </h4>
                                <p className="mt-1 text-sm text-muted-foreground">
                                    Configure your FTP server connection
                                    details.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label htmlFor="ftpHost">Host</Label>
                                    <Field
                                        as={Input}
                                        id="ftpHost"
                                        name="ftpHost"
                                        placeholder="ftp.example.com"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="ftpPort">Port</Label>
                                    <Field
                                        as={Input}
                                        id="ftpPort"
                                        name="ftpPort"
                                        placeholder="21, 22, or 990"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label htmlFor="ftpUser">Username</Label>
                                    <Field
                                        as={Input}
                                        id="ftpUser"
                                        name="ftpUser"
                                        placeholder="Enter FTP username"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="ftpPassword">
                                        Password
                                    </Label>
                                    <Field
                                        as={Input}
                                        id="ftpPassword"
                                        name="ftpPassword"
                                        type="password"
                                        placeholder="Enter FTP password"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="ftpDirectory">Directory</Label>
                                <Field
                                    as={Input}
                                    id="ftpDirectory"
                                    name="ftpDirectory"
                                    placeholder="/path/to/upload/directory"
                                />
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-4 pt-6 border-t border-border">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => listFTPDirectories(galleryId)}
                            >
                                Test Connection
                            </Button>
                            <Button
                                type="submit"
                                disabled={props.isSubmitting}
                            >
                                {props.isSubmitting
                                    ? 'Saving...'
                                    : 'Save Settings'}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
}
